<!--suppress ALL -->
<template>
  <div class="control g-input-wrp" :class="controlClassName">
    <input v-if="inputType !== 'textarea'" :type="newInputType" :class="className"
           :value="showRealValue ? newValue : newValueCurrency" v-bind="$attrs"
           v-on="inputListeners" @focus="onFocus" @blur="onBlur" :maxlength="maxlength" ref="input">
    <textarea v-else :class="className" :value="newValue" v-bind="$attrs" :maxlength="maxlength"
              v-on="inputListeners" @focus="onFocus" @blur="onBlur" ref="textarea"></textarea>
    <span v-if="$slots.iconLeft" :class="`icon is-left ${size ? ('is-'+size) : ''}`">
            <slot name="iconLeft"></slot>
        </span>
    <span v-if="$slots.iconRight || $scopedSlots['iconRight']"
          :class="`icon is-right ${size ? ('is-'+size) : ''}`">
            <slot name="iconRight" :revealPassword="revealPassword"
                  :toggleRevealPassword="toggleRevealPassword"></slot>
        </span>
    <span v-if="maxlength && isFocused && (inputType === 'textarea' || inputType === 'text') ">
            <p class="help count-help">{{ countValue }}/{{ maxlength }}</p>
        </span>
    <span v-if="inputType === 'password' && !hideReveal" class="checkbox-show-pwd">
            <input type="checkbox" v-model="revealPassword"> <span class="help">Show password</span>
        </span>
    <slot></slot>
  </div>
</template>

<script lang="ts">
import { Component, Mixin, Prop, Watch } from "annotation";
import FormPropMixins from "../../../../util/mixin/FormProperties";
import currency from "../../../../util/filter/currency";

@Component
export default class GInput extends Mixin(FormPropMixins) {

  @Prop()
  public value: any;

  @Prop({ default: "text" })
  public inputType: string;

  @Prop({ default: "", type: String })
  public label: string;

  @Prop({ default: null, type: String })
  public currencyFormat: string;

  @Prop({ default: "", type: String })
  public message: string;

  @Prop({ default: false, type: Boolean })
  public hideReveal: boolean;

  @Prop({ default: false, type: Boolean })
  public isStatic: boolean;

  @Watch("value", { immediate: true })
  public onValueChange(value) {
    // In currency mode, always store as number to prevent type oscillation
    // between string (from user input) and number (from parent prop).
    if (this.currencyFormat) {
      const numVal = parseInt(String(value).replace(/[^0-9]/g, '')) || 0;
      this.newValue = numVal;
    } else {
      this.newValue = value;
    }
  }

  @Watch("newValue")
  public onNewValueChange(value) {
    // We already emit in onInput, but we need this for programmatic changes to newValue
    // However, we should be careful about infinite loops.
    // Usually, newValue is changed by onInput or onValueChange.
    // Let's just keep it simple for now and rely on onInput for user interaction.
  }

  public get newValueCurrency() {
    // CRITICAL: Read from the prop (this.value), NOT this.newValue.
    // this.value is the authoritative source of truth from the parent component.
    // this.newValue can have Vue 2 computed caching issues during blur transitions.
    let val = this.value;
    if (typeof val === "string" && this.currencyFormat) {
      val = val.replace(/[^0-9]/g, "");
    }
    const result = currency(parseInt(val as any) || 0, this.currencyFormat);
    return result;
  }

  public get showRealValue() {
    return (this.currencyFormat && !this.isFocused) ? false : true;
  }

  public newValue: any = this.value;

  public revealPassword: boolean = false;

  public get newInputType() {
    const isPassword = this.inputType === "password" && this.revealPassword;
    const isCurrency = !this.showRealValue;

    return isPassword || isCurrency ? "text" : this.inputType;
  }

  public get alterClassName() {
    if (this.$parent.isFieldComponent) {
      const { type, size } = this.$parent;
      return [type, size].reduce((accu, item) => accu + (item ? ` is-${item}` : ""), "");
    }

    return "";
  }

  public countValue: number = 0;

  public get className() {
    const { type, size, shape, isFocused, isStatic, alterClassName, inputType }: any = this;
    const finalClassName = [type, size, shape].reduce((accu, item) => accu + (item ? `is-${item} ` : ""), "");

    return [
      "g-input", (inputType !== "textarea" ? "input" : "textarea"),
      finalClassName, { "is-focused": isFocused, "is-static": isStatic },
      alterClassName
    ];
  }

  public get controlClassName() {
    const { isLoading, size, isExpanded, $scopedSlots, $slots, inputType } = this;
    const { iconLeft, iconRight } = $slots;
    const { iconRight: hasIconRight } = $scopedSlots;
    // if checkbox show password showed. set control class to overflow auto
    const overflowAuto = inputType === "password";

    return [{
      "ov-auto": overflowAuto,
      "is-loading": isLoading,
      "has-icons-left": iconLeft,
      "is-expanded": isExpanded,
      "has-icons-right": iconRight || hasIconRight
    }, (size ? ("is-" + size) : "")];
  }

  public get inputListeners() {
    return Object.assign({}, this.$listeners, {
      input: (event: any) => {
        this.onInput(event);
      }
    });
  }

  public onInput(event: any) {
    const rawValue = event && event.target ? event.target.value : event;
    
    // CRITICAL: If we are not focused, this is likely a programmatic update 
    // from Vue or a directive (like v-unicode) during blur/re-render.
    // We must NOT sync formatted strings back to newValue.
    if (this.currencyFormat && !this.isFocused) {
      return;
    }

    // SAFEGUARD: If we are focused but the value contains letters/symbols 
    // and we expect currency (numbers only), it's likely a misfire.
    if (this.currencyFormat && this.isFocused && /[a-zA-Z]/.test(String(rawValue))) {
      return;
    }

    // In currency mode, always work with numeric values to prevent
    // type oscillation between string/number that causes reset loops.
    if (this.currencyFormat) {
      const numVal = parseInt(String(rawValue).replace(/[^0-9]/g, '')) || 0;
      if (this.newValue === numVal) {
        return;
      }
      this.newValue = numVal;
      this.countValue = String(numVal).length;
      this.$emit("input", numVal);
    } else {
      if (this.newValue === rawValue) {
        return;
      }
      this.newValue = rawValue;
      this.countValue = (rawValue && rawValue.length) ? rawValue.length : 0;
      this.$emit("input", rawValue);
    }
  }

  public onBlur(event: any) {
    this.isFocused = false;
    // Force-sync newValue from the prop on blur.
    // This ensures the raw value is correct when the user re-focuses.
    if (this.currencyFormat) {
      const numVal = parseInt(String(this.value).replace(/[^0-9]/g, '')) || 0;
      this.newValue = numVal;
    }
    this.$emit("blur", event);
  }

  public toggleRevealPassword() {
    this.revealPassword = !this.revealPassword;
  }

  /**
   * RE-EVALUATED: Aggressive updated() sync removed.
   * With robust parsing in the parent, Vue 2's native :value binding 
   * should be sufficient. Excessive manual DOM manipulation often 
   * leads to cursor jumping and race conditions in production.
   */
  public updated() {}
}
</script>

<style lang="scss">
.checkbox-show-pwd {
  display: inline-flex;
  align-items: center;
  float: right;
  margin-top: .50rem;

  input[type="checkbox"] {
    margin-right: .3rem;
  }

  .help {
    display: inherit;
    margin-top: 0;
  }
}

.ov-auto {
  overflow: auto;
}
</style>
